'use strict';

var d3 = require('d3');
var _ = require('lodash');
var YAxis = function YAxis(d3Helper, constants, OverlappingResolutionService) {
  /**
   * @param yAxisTickFormatter - a formatter for displaying tickers
   * @param {ChartElementConstants.AXIS.MODE} mode - defines on which side of chart axis should be displayed. Except that, remember to change padding in params
   * @returns {Function}
   * @constructor
   */
  var Axis = function Axis(yAxisTickFormatter, mode) {
    var params, scales, axisLabel;
    var noHorizontalLines = false;
    var horizontalLinesValues = null;
    var tickValues;
    var curveLabels;
    var labelRotation;
    var isHorizontalShiftAllowed = true;
    var isLeftAxis = mode === constants.AXIS.MODE.LEFT;
    var isRightAxis = mode === constants.AXIS.MODE.RIGHT;

    /**
     * @param container
     */
    function createMask(container) {
      if (container.select('.mask').empty()) {
        container.append('rect').attr('class', 'mask').attr({
          x: isLeftAxis ? -params.padding.left : -4,
          y: -5,
          width: isLeftAxis ? params.padding.left + 4 : params.padding.right + 4,
          height: params.height + 10,
          fill: 'white'
        });
      }
    }
    function getShiftValue(leftParamName, rightParamName) {
      if (isLeftAxis) {
        return -params[leftParamName];
      } else {
        var axisShiftParam = _.isNil(params[rightParamName]) ? params[leftParamName] : params[rightParamName];
        return isHorizontalShiftAllowed ? axisShiftParam + params.width : axisShiftParam;
      }
    }

    /**
     * Constructs axis DOM element
     * @param container
     */
    function createYAxis(container) {
      var yAxis = d3.svg.axis().scale(scales.yScale).orient(mode).ticks(params.yNumberOfTicks).tickFormat(yAxisTickFormatter);
      if (!_.isUndefined(params.yAxisTickSize)) {
        yAxis.tickSize(params.yAxisTickSize);
      }
      if (Array.isArray(tickValues) || tickValues === null) {
        yAxis.tickValues(tickValues);
      }
      var xShift = getShiftValue('yAxisShift', 'yAxisRightShift');
      d3Helper.createGroupElement(container, 'y-axis-' + mode).call(createMask).call(yAxis).attr('transform', 'translate(' + [xShift, 0] + ')');
    }

    /**
     * Constructs labels DOM element
     * @param container
     */
    function yAxisText(container) {
      if (!axisLabel) {
        return;
      }
      var label = container.selectAll('.y-axis-label-' + mode).data([null]);
      label.enter().append('text');
      var rightLabelRotation = params.yAxisLabelRotation || 90;
      var rotation = labelRotation || (isLeftAxis ? -90 : rightLabelRotation);
      var xShift = getShiftValue('yAxisLabelShift', 'yAxisRightLabelShift');
      label.text(axisLabel).attr({
        'class': 'y-axis-label-' + mode,
        'transform': 'translate(' + [xShift, params.height / 2] + ') rotate(' + rotation + ')'
      });
    }
    function calculateLinesData() {
      var linesData;
      var lineShift = 0;
      if (horizontalLinesValues) {
        linesData = horizontalLinesValues;
      } else if (scales.yScale.ticks) {
        linesData = scales.yScale.ticks(params.yNumberOfTicks);
      } else {
        linesData = scales.yScale.domain();
        if (linesData.length > 1) {
          var range = scales.yScale.range();
          lineShift = (range[1] - range[0] - scales.yScale.rangeBand()) / 2 + scales.yScale.rangeBand();
        }
      }
      return {
        data: linesData,
        shift: lineShift
      };
    }

    /**
     * Constructs helper horizontal lines
     * @param container
     */
    function createHorizontalLines(container) {
      if (noHorizontalLines) {
        return;
      }
      var gElem = d3Helper.createGroupElement(container, 'y-axis-lines');
      var leftPadding = isLeftAxis ? params.yAxisShift + 3 : 0;
      var rightPadding = isRightAxis ? params.yAxisShift + 3 : 0;
      var rangeExtent = scales.xScale.rangeExtent ? scales.xScale.rangeExtent() : scales.xScale.range();
      var linesData = calculateLinesData();
      gElem.selectAll('line').remove();
      var lines = gElem.selectAll('line').data(linesData.data);
      lines.enter().append('line').attr('class', function (d) {
        return 'y-axis-' + mode + '-line-' + d;
      });
      lines.exit().remove();
      function calculateYPosition(value) {
        return scales.yScale(value) + linesData.shift;
      }
      gElem.selectAll('line').attr(d3Helper.roundCoordinates({
        'x1': rangeExtent[0] - leftPadding,
        'y1': calculateYPosition,
        'x2': rangeExtent[1] + rightPadding,
        'y2': calculateYPosition
      }, ['x1', 'y1', 'x2', 'y2']));
    }

    /**
     * Draws single Y axis label
     * @param groupContainer
     * @param labelData
     * @param height
     * @param width
     */
    function drawYAxisLabel(groupContainer, labelData, height, width) {
      var labelShift = isLeftAxis ? -width : 0;
      groupContainer.append('rect').attr({
        x: labelShift,
        y: labelData.textY - height / 2,
        width: width,
        height: height,
        "class": labelData.className
      });
      groupContainer.append('text').text(labelData.label).attr({
        x: labelShift + width / 2,
        y: labelData.textY
      });
    }

    /**
     * Draws Y axis labels
     * @param container
     * @param dataPoints
     * @param width
     * @param height
     */
    function drawYAxisLabels(container, dataPoints, width, height) {
      var yAxisGroup = container.select('g.y-axis-' + mode);
      var curveLabelsGroup = d3Helper.createGroupElement(yAxisGroup, 'y-axis-curve-label-group');
      curveLabelsGroup.selectAll('*').remove();
      dataPoints.forEach(function (d, index) {
        if (d.labels) {
          var multiLineLabelsGroup = d3Helper.createGroupElement(curveLabelsGroup, 'multi-line-label-group index-' + index);
          _.forEach(d.labels, function (labelData) {
            drawYAxisLabel(multiLineLabelsGroup, labelData, height, width);
          });
        } else {
          var singleLineLabelsGroup = d3Helper.createGroupElement(curveLabelsGroup, 'single-line-label-group index-' + index);
          drawYAxisLabel(singleLineLabelsGroup, d, height, width);
        }
      });
    }

    /**
     * Decorates dataPoints with different display attributes
     * @param dataPoints
     * @param width
     * @param height
     */
    function decorateWithDisplayAttributes(dataPoints, width, height) {
      _.forEach(dataPoints, function (dataPoint) {
        var originalX = 0;
        var originalY = scales.yScale(dataPoint.value);
        _.assign(dataPoint, {
          originalX: originalX,
          originalY: originalY,
          x: 0,
          y: originalY - height / 2,
          textX: width / 2,
          textY: originalY,
          width: width,
          height: height,
          className: dataPoint["class"],
          label: dataPoint.caption
        });
      });
    }

    /**
     * Simple loop which resolves all overlapping between drawn labels
     * @param dataPoints
     * @param width
     * @param height
     */
    function resolveOverlapping(dataPoints, width, height) {
      OverlappingResolutionService.resolveOverlapping({
        dataPoints: dataPoints,
        sortingBy: ['yPropertyValue', 'className'],
        sortingOrder: ['desc', 'asc'],
        labelPropertyName: 'caption',
        xPropertyName: undefined,
        yPropertyName: 'value',
        indicatorSizing: {
          INDICATOR_SIZE: height,
          HALF_INDICATOR_SIZE: height / 2,
          MARGIN: 0
        },
        params: params
      });
    }

    /**
     * Creates Y axis labels with overlapping detection and resolution
     * @param container
     */
    function createYAxisLabels(container) {
      if (!curveLabels) {
        return;
      }
      var height = 20;
      var width = (isLeftAxis ? params.padding.left : params.padding.right) - params.yAxisShift;
      var clonedCurveLabels = _.cloneDeep(curveLabels);
      decorateWithDisplayAttributes(clonedCurveLabels, width, height);
      resolveOverlapping(clonedCurveLabels, width, height);
      drawYAxisLabels(container, clonedCurveLabels, width, height);
    }

    /**
     *
     * @param container
     */
    function yAxis(container) {
      var axesGroup = d3Helper.createGroupElement(container, 'group-axes');
      axesGroup.call(createYAxis).call(createHorizontalLines).call(createYAxisLabels).call(yAxisText);
    }

    /**
     *  Setters and getters
     */

    yAxis.label = function (value) {
      axisLabel = value;
      return yAxis;
    };
    yAxis.withHorizontalLinesValues = function (values) {
      horizontalLinesValues = values;
      return yAxis;
    };
    yAxis.withCurveLabel = function (values) {
      curveLabels = values;
      return yAxis;
    };
    yAxis.withoutHorizontalLines = function () {
      noHorizontalLines = true;
      return yAxis;
    };
    yAxis.withTickValues = function (v) {
      tickValues = v;
      return yAxis;
    };
    yAxis.withLabelRotation = function (v) {
      labelRotation = v;
      return yAxis;
    };
    yAxis.params = function (value) {
      params = value;
      return yAxis;
    };
    yAxis.scales = function (value) {
      scales = value;
      return yAxis;
    };
    yAxis.withoutHorizontalShift = function () {
      isHorizontalShiftAllowed = false;
      return yAxis;
    };
    return yAxis;
  };
  return Axis;
};
YAxis.$inject = ['d3Helper', 'ChartElementsConstants', 'OverlappingResolutionService'];
module.exports = YAxis;